// The printable invoice, laid out to match the shop's existing paper design:
// logo + script wordmark, letterspaced INVOICE, a pink-banded line-item
// table, a script fulfillment note, totals, and a pink footer carrying the
// business details and payment methods.
//
// Rendered both on the customer's invoice page and in the admin preview, so
// what the admin approves is exactly what the customer sees. Print styles
// live in index.css under @media print. Responsive classes use `wide:`
// rather than `sm:` so the admin can render the full layout to an image
// from a phone (see the variant in tailwind.config.js).
import React from 'react';
import type { Order, PaymentMethodOption } from '../types';
import { formatCents } from '../utils/money';
import badge from '../assets/bleums-badge.png';

interface Props {
  order: Order;
  paymentMethods: PaymentMethodOption[];
  /**
   * Mockup/proof image. Passed explicitly by the admin preview so a
   * just-uploaded image shows before it's saved to the order; otherwise
   * falls back to whatever is stored.
   */
  sampleImageUrl?: string;
  /** Shop contact block, from admin settings. */
  business?: {
    name?: string;
    contact_name?: string;
    email?: string;
    social?: string;
    /** Per-platform handles. Fall back to `social` when unset. */
    instagram?: string;
    tiktok?: string;
    /** Where a pickup order is collected from, or dropped off. */
    pickup_address?: string;
  };
}

/** The script note printed for each fulfillment, absent an invoice note. */
const FULFILLMENT_NOTES: Record<Order['fulfillment'], string> = {
  pickup: 'local pickup',
  dropoff: 'local drop-off',
  shipping: 'shipping',
  none: '',
};

/** The footer's contact block, from the public settings map. */
export const invoiceBusiness = (settings: Record<string, string>): Props['business'] => ({
  name: settings.business_name,
  contact_name: settings.business_contact_name,
  email: settings.business_email,
  social: settings.business_social,
  instagram: settings.instagram_handle,
  tiktok: settings.tiktok_handle,
  pickup_address: settings.pickup_address,
});

/**
 * Turn a handle into a profile URL.
 *
 * Takes whatever the shop typed into settings: "@bleumscreations",
 * "bleumscreations", or a full URL pasted from the address bar. Returns ''
 * for anything empty, which the caller uses to render plain marks rather
 * than a link to nowhere.
 */
const socialUrl = (base: string, handle?: string): string => {
  const raw = (handle ?? '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const name = raw.replace(/^@+/, '').replace(/\/+$/, '');
  return name ? `${base}${name}` : '';
};

/**
 * The mark next to a payment method: an uploaded logo if the shop has set
 * one, otherwise the emoji.
 *
 * Both render in the same square regardless of the source image's shape or
 * resolution, so a row of logos lines up instead of stepping up and down
 * with whatever each brand publishes. The emoji doubles as the logo's alt
 * text, so a printout or an email client that won't load images still
 * shows something.
 */
const MethodMark: React.FC<{ method: PaymentMethodOption }> = ({ method }) => {
  if (method.logo_url) {
    return (
      <img
        src={method.logo_url}
        alt={method.emoji || ''}
        className="inline-block h-4 w-4 object-contain align-[-2px] mr-1"
      />
    );
  }
  return method.emoji ? <span className="mr-1">{method.emoji}</span> : null;
};

// 08.01.26 — the format the paper invoices use.
const shortDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}.${p(d.getDate())}.${String(d.getFullYear()).slice(2)}`;
};

const InstagramMark: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor">
    <path d="M12 2.2c3.2 0 3.6 0 4.9.07 1.2.05 1.8.25 2.2.42.6.22 1 .48 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c0 1.2-.2 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2 0-1.8-.2-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c0-1.2.2-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.07-1.1.05-1.7.24-2.1.4-.5.2-.9.44-1.2.78-.34.33-.57.7-.78 1.2-.16.4-.35 1-.4 2.1C2.75 9.75 2.75 10.1 2.75 12s0 2.25.07 3.45c.05 1.1.24 1.7.4 2.1.2.5.44.87.78 1.2.33.34.7.57 1.2.78.4.16 1 .35 2.1.4 1.2.07 1.6.07 4.7.07s3.5 0 4.7-.07c1.1-.05 1.7-.24 2.1-.4.5-.2.87-.44 1.2-.78.34-.33.57-.7.78-1.2.16-.4.35-1 .4-2.1.07-1.2.07-1.55.07-3.45s0-2.25-.07-3.45c-.05-1.1-.24-1.7-.4-2.1a3.2 3.2 0 0 0-.78-1.2 3.2 3.2 0 0 0-1.2-.78c-.4-.16-1-.35-2.1-.4C15.5 4 15.1 4 12 4z" />
    <path d="M12 7.2a4.8 4.8 0 1 0 0 9.6 4.8 4.8 0 0 0 0-9.6zm0 7.9a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2z" />
    <circle cx="17.1" cy="6.9" r="1.15" />
  </svg>
);

const TikTokMark: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor">
    <path d="M16.5 2h-2.9v12.4a2.3 2.3 0 1 1-1.9-2.26V9.2a5.2 5.2 0 1 0 4.8 5.18V8.63a6.3 6.3 0 0 0 3.6 1.13V6.86a3.5 3.5 0 0 1-3.6-3.35V2z" />
  </svg>
);

const InvoiceDocument: React.FC<Props> = ({
  order,
  paymentMethods,
  sampleImageUrl,
  business,
}) => {
  const sample = sampleImageUrl ?? order.invoice_sample_image_url;
  const accepted = (order.accepted_payment_methods ?? [])
    .map(key => paymentMethods.find(m => m.key === key))
    .filter((m): m is PaymentMethodOption => Boolean(m))
    // Card is a link, not a "send money here" instruction; it's surfaced as
    // a button on the web page instead of in this printed block.
    .filter(m => m.details);

  // Empty for 'none' with no description, which drops the note entirely.
  const fulfillmentNote = order.description?.trim() || FULFILLMENT_NOTES[order.fulfillment];

  // Drawn on the document itself, not just the page around it, so it
  // survives printing, saving as PDF and being screenshotted to send on.
  const isPaid = Boolean(order.paid_at);
  const paidOn = shortDate(order.paid_at);

  const instagramUrl = socialUrl(
    'https://instagram.com/',
    business?.instagram || business?.social
  );
  const tiktokUrl = socialUrl('https://tiktok.com/@', business?.tiktok || business?.social);

  return (
    <article className="invoice-doc bg-white text-text mx-auto w-full max-w-[850px] shadow-sm print:shadow-none">
      {/* Header */}
      <header className="flex items-center gap-4 wide:gap-6 px-5 wide:px-10 pt-6 wide:pt-10 print:pt-0">
        <img src={badge} alt="" aria-hidden="true" className="h-20 w-20 wide:h-28 wide:w-28 print:h-20 print:w-20 object-contain shrink-0" />
        <div className="font-display text-4xl wide:text-6xl print:text-5xl leading-none text-text">
          Bleums Creations
        </div>
      </header>

      <div className="px-5 wide:px-10 pb-6 wide:pb-10 print:pb-6">
        <div className="flex items-center justify-between gap-4 mt-8 mb-8 print:mt-4 print:mb-4">
          <h1 className="text-3xl wide:text-4xl font-sans font-light tracking-[0.35em]">
            INVOICE
          </h1>
          {isPaid && (
            <div className="border-2 border-green-700 text-green-700 rounded px-4 py-1 -rotate-6">
              <div className="text-2xl font-bold tracking-[0.2em]">PAID</div>
              {paidOn && <div className="text-[10px] tracking-wide text-center">{paidOn}</div>}
            </div>
          )}
        </div>

        {/* Bill-to and invoice meta */}
        <div className="flex justify-between gap-8 flex-wrap">
          <div className="text-sm">
            <div className="font-bold tracking-wide">INVOICE TO:</div>
            <div className="font-bold mt-3 uppercase">{order.customer_name}</div>
            {order.fulfillment === 'shipping' && order.shipping_address && (
              <div className="text-text-light mt-1 leading-relaxed">
                {order.shipping_address.line1}
                {order.shipping_address.line2 && <><br />{order.shipping_address.line2}</>}
                <br />
                {order.shipping_address.city}, {order.shipping_address.state}{' '}
                {order.shipping_address.postal_code}
              </div>
            )}

            {/* A pickup order has no shipping address, and the customer
                still needs to be told where to come. The shop's one
                address covers both directions, so it's labelled for
                either. Skipped when no address is configured rather than
                printing an empty heading. */}
            {order.fulfillment === 'pickup' && business?.pickup_address && (
              <div className="mt-4">
                <div className="font-bold tracking-wide">PICKUP / DROP-OFF:</div>
                <div className="text-text-light mt-1 leading-relaxed whitespace-pre-line">
                  {business.pickup_address}
                </div>
              </div>
            )}
          </div>
          <div className="text-sm text-right leading-relaxed">
            <div>
              INVOICE NUMBER:{' '}
              <span className="font-medium">
                #{String(order.invoice_number ?? 0).padStart(3, '0')}
              </span>
            </div>
            <div>INVOICE DATE: {shortDate(order.invoice_date ?? order.created_at)}</div>
            {order.due_date && <div>DUE DATE: {shortDate(order.due_date)}</div>}
          </div>
        </div>

        {/* Line items */}
        <table className="w-full mt-10 print:mt-6 text-sm">
          <thead>
            <tr className="bg-[#FBE4EC]">
              <th className="text-left font-bold tracking-wide px-3 py-2">ITEM DESCRIPTION</th>
              <th className="text-center font-bold tracking-wide px-3 py-2 w-20">QTY</th>
              <th className="text-right font-bold tracking-wide px-3 py-2 w-28">PRICE</th>
              <th className="text-right font-bold tracking-wide px-3 py-2 w-28">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((line, i) => (
              <tr key={i} className="align-top">
                <td className="px-3 pt-4 print:pt-2">
                  <div className="uppercase">{line.product_name}</div>
                  {line.description && (
                    <div className="pl-4 text-text-light">{line.description}</div>
                  )}
                  {line.personalization && Object.keys(line.personalization).length > 0 && (
                    <div className="pl-4 text-text-light">
                      {Object.entries(line.personalization)
                        .map(([, value]) => value)
                        .join(', ')}
                    </div>
                  )}
                  {line.notes && <div className="pl-4 text-text-light italic">{line.notes}</div>}
                </td>
                <td className="px-3 pt-4 print:pt-2 text-center">{line.qty}</td>
                <td className="px-3 pt-4 print:pt-2 text-right">{formatCents(line.unit_price_cents)}</td>
                <td className="px-3 pt-4 print:pt-2 text-right">{formatCents(line.line_total_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {sample && (
          <div className="mt-8 print:mt-4">
            <div className="text-xs font-bold tracking-wide mb-2">PROOF / MOCKUP</div>
            <img
              src={sample}
              alt="Proof of the finished piece"
              className="max-h-56 print:max-h-40 max-w-[280px] w-auto object-contain rounded border border-background-dark"
            />
          </div>
        )}

        {/* Fulfillment / free-text note, in the shop's script hand */}
        {fulfillmentNote && (
          <div className="text-right pr-6 mt-10 mb-6 print:mt-4 print:mb-3">
            <span className="font-display text-3xl wide:text-4xl text-[#E48BAA]">{fulfillmentNote}</span>
          </div>
        )}

        {/* Totals */}
        <div className="border-t-2 border-text pt-6 print:pt-4 flex flex-col wide:flex-row wide:justify-between wide:items-start gap-4 wide:gap-8">
          <div className="font-display text-4xl wide:text-6xl print:text-5xl leading-none">Thank You!</div>
          <table className="text-sm">
            <tbody>
              <tr>
                <td className="font-bold tracking-wide text-right pr-6 py-0.5">SUBTOTAL</td>
                <td className="text-right py-0.5">{formatCents(order.subtotal_cents)}</td>
              </tr>
              {order.shipping_cents > 0 && (
                <tr>
                  <td className="font-bold tracking-wide text-right pr-6 py-0.5">SHIPPING</td>
                  <td className="text-right py-0.5">{formatCents(order.shipping_cents)}</td>
                </tr>
              )}
              {order.discount_cents > 0 && (
                <tr>
                  <td className="font-bold tracking-wide text-right pr-6 py-0.5">DISCOUNT</td>
                  <td className="text-right py-0.5">−{formatCents(order.discount_cents)}</td>
                </tr>
              )}
              {/* Omitted entirely when nothing is being taxed, rather than
                  printing a zero line the customer has to interpret. */}
              {order.tax_cents > 0 && (
                <tr>
                  <td className="font-bold tracking-wide text-right pr-6 py-0.5">
                    TAX
                    {order.tax_rate_percent ? (
                      <span className="font-normal text-text-light">
                        {' '}({order.tax_rate_percent}%)
                      </span>
                    ) : null}
                  </td>
                  <td className="text-right py-0.5">{formatCents(order.tax_cents)}</td>
                </tr>
              )}
              {/* The line everyone actually reads, sized and weighted to
                  match. Reads "amount paid" once it's settled, so a
                  printed or forwarded copy isn't still demanding money. */}
              <tr>
                <td className="font-bold tracking-wide text-right pr-6 pt-2 text-base">
                  {isPaid ? 'AMOUNT PAID' : 'AMOUNT DUE'}
                </td>
                <td className="text-right pt-2 text-lg font-bold">
                  {formatCents(order.total_cents)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#FBE4EC] px-5 wide:px-10 py-6 print:py-4 flex flex-col wide:flex-row wide:justify-between gap-6 wide:gap-8 text-sm">
        <div>
          <div className="font-bold tracking-wide">
            {(business?.name ?? 'Bleums Creations').toUpperCase()}
          </div>
          {business?.contact_name && <div className="mt-1">{business.contact_name}</div>}
          {business?.email && <div>{business.email}</div>}
          {/* Each mark links to its own profile. The handles fall back to
              the single `social` setting, which is the common case — one
              name on both platforms — so this needs no extra setup to
              work. A platform with no handle keeps the mark unlinked
              rather than pointing at a profile that may not exist. */}
          {business?.social && (
            <div className="mt-2 flex items-center gap-2">
              {instagramUrl ? (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="hover:opacity-70 transition"
                >
                  <InstagramMark />
                </a>
              ) : (
                <InstagramMark />
              )}
              {tiktokUrl ? (
                <a
                  href={tiktokUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="TikTok"
                  className="hover:opacity-70 transition"
                >
                  <TikTokMark />
                </a>
              ) : (
                <TikTokMark />
              )}
              {instagramUrl ? (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="uppercase hover:underline"
                >
                  {business.social}
                </a>
              ) : (
                <span className="uppercase">{business.social}</span>
              )}
            </div>
          )}
        </div>
        <div className="wide:text-right">
          <div className="font-bold tracking-wide">PAYMENT METHODS</div>
          {accepted.length > 0 ? (
            accepted.map(m => (
              <div key={m.key} className="mt-1">
                <MethodMark method={m} />
                {m.label.toUpperCase()}: {m.details}
              </div>
            ))
          ) : (
            <div className="mt-1">Reply to this invoice to arrange payment.</div>
          )}
        </div>
      </footer>
    </article>
  );
};

export default InvoiceDocument;
